import { Children } from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { DropdownMenu } from '@components/DropdownMenu/DropdownMenu';

import type { ComponentProps, ReactElement } from 'react';
import { IconButton } from '@components/IconButton/IconButton';
import type { ActionItemProps } from './ActionItem';
import { ActionItem } from './ActionItem';

export interface ActionMenuProps {
  /**
   * Styles override.
   */
  className?: string;
  /**
   * Accessible description of the `ActionMenu`. Shown in a tooltip and in ARIA attributes.
   * Provides context for the dropdown trigger. Used only in the dropdown mode.
   */
  description: string;
  /**
   * List of `ActionMenu.Item` components.
   */
  children:
    | ReactElement<ComponentProps<typeof ActionItem>, 'ActionItem'>[]
    | ReactElement<ComponentProps<typeof ActionItem>, 'ActionItem'>;
}

const SHOWN_LIMIT = 3;

const StyledIconButton = styled(IconButton)`
  margin-left: ${tokens.spacer.x0};

  :first-of-type {
    margin-left: 0;
  }
`;

const Container = styled.div``;

/**
 * ActionMenu component.
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-actionmenu--action-menu Code documentation}
 *
 * @example
 * <ActionMenu description="">
 *   <ActionMenu.Item
 *     icon="pencil"
 *     onClick={onItemClick}
 *     description="Edit item"
 *   />
 *   <ActionMenu.Item
 *     icon="bell"
 *     onClick={onItemClick}
 *     description="Activate alerts"
 *   />
 * </ActionMenu>
 */
export const ActionMenu = ({
  description,
  children,
  className,
}: ActionMenuProps): JSX.Element => {
  const actionsCount = Children.count(children);
  const renderMode = actionsCount > SHOWN_LIMIT ? 'list' : 'item';

  if (renderMode === 'item') {
    return (
      <Container className={className}>
        {Children.map(children, (child: ReactElement<ActionItemProps>) => {
          if (child.type === ActionItem) {
            return (
              <StyledIconButton
                key={child.props.icon}
                icon={child.props.icon}
                kind="ghost"
                size="s"
                description={child.props.description}
                tooltipText={child.props.description}
                isDisabled={child.props.isDisabled}
                onClick={child.props.onClick}
              />
            );
          }
        })}
      </Container>
    );
  }

  return (
    <DropdownMenu
      trigger={
        <IconButton
          kind="ghost"
          size="s"
          icon="menu_kebab"
          tooltipText={description}
        />
      }
    >
      {Children.map(children, (child: ReactElement) => {
        if (child.type === ActionItem) {
          return (
            <DropdownMenu.Item
              key={child.props.icon}
              icon={child.props.icon}
              onClick={child.props.onClick}
              isDisabled={child.props.isDisabled}
            >
              {child.props.description}
            </DropdownMenu.Item>
          );
        }
      })}
    </DropdownMenu>
  );
};

ActionMenu.Item = ActionItem;
ActionMenu.displayName = 'ActionMenu';
